/**
 * The leasing programme's fixed parameters.
 *
 * THE SPEC IS THE PUBLISHED DOC, not the Python that used to implement it:
 * https://swarm-gitbook-staging.up.railway.app/agent-licenses/agent-license-leasing-program/
 * "Leasing Weight Framework" — base weight, Commitment Term Factor, Volume-Based
 * Weighting. `apps/jobs/src/leasing/weight.ts` implements the formula and its
 * header records where the transcribed Python disagreed.
 *
 * The values themselves were transcribed from `LicenseStakingManager.__init__` in
 * `swarm-license-staking-api` (`license_staking/staking_rewards.py`), where they
 * were instance attributes on the manager and therefore invisible to the FastAPI
 * routes that also needed them; each one matches the published table.
 *
 * They live in `packages/shared` because BOTH workspaces read them and the split
 * rule says so: `apps/jobs` pays the pool out every night, `apps/api` quotes the
 * same pool back to the lease picker (`/api/license-leases/network-stats`) and
 * reports it on `/api/v1/admin/stats`. A copy in each app is a copy that can
 * drift, and a drift here means the projection shown before signing does not
 * match the reward actually paid.
 *
 * None of these is configuration. Changing one is a tokenomics decision, and it
 * has to land in the same commit on both sides — which is exactly what a shared
 * module forces.
 */

/**
 * $TRUTH split across every active lease, per day.
 *
 * 136,986.3 x 730 days ~= the 100M token leasing allocation over two years. The
 * figure is flat, NOT halving: the ownership/referral stream in
 * `apps/jobs/src/emission.ts` halves on a 730-day clock, the leasing stream does
 * not, and conflating the two would double the leasing payout in epoch 1.
 */
export const DAILY_LEASING_POOL = 136_986.3;

/** Days the leasing allocation is spread over. Two years. */
export const LEASING_POOL_DAYS = 730;

/**
 * Ceiling the job checks before it distributes anything.
 *
 * `_verify_daily_leasing_pool_limit` in the Python original: if the accumulated
 * rewards across all active leases already exceed the whole programme, the day
 * is skipped rather than overspent.
 */
export const TOTAL_LEASING_POOL = DAILY_LEASING_POOL * LEASING_POOL_DAYS;

/** Lease terms, in months. Mirrors the `license_leases_duration_months_check`. */
export const LEASE_DURATION_MONTHS = [6, 12, 18] as const;

/** One of the three accepted lease terms. */
export type LeaseDurationMonths = (typeof LEASE_DURATION_MONTHS)[number];

/**
 * "Commitment Term Factor" — the first of the two multiplicative parameters.
 *
 * Published table:
 *   6 months   1.0x   minimum threshold
 *   12 months  2.0x   doubled rewards for a one-year commitment
 *   18 months  3.0x   maximum weighting for the highest commitment
 */
export const LEASE_TERM_FACTORS: Record<LeaseDurationMonths, number> = {
  6: 1.0,
  12: 2.0,
  18: 3.0,
};

/**
 * "Base Leasing Weight" — 100,000 units, per LEASE.
 *
 * The spec is explicit and the wording is load-bearing: every participant gets
 * this "regardless of lease duration or number of licenses committed". It is NOT
 * per license. The two factors below are what scale it; multiplying the base by
 * the license count as well would apply volume twice.
 */
export const LEASE_BASE_WEIGHT = 100_000;

/** "Volume-Based Weighting" scales linearly: each leased license contributes +0.2x. */
export const LEASE_VOLUME_FACTOR_STEP = 0.2;

/** The volume factor caps at 10.0x, reached at 50 licenses. */
export const LEASE_VOLUME_FACTOR_MAX = 10.0;

/**
 * Days added per month when a lease's `end_date` is computed at creation.
 *
 * A flat 30, which is what the Python did (`timedelta(days=duration_months * 30)`)
 * and therefore what every existing row in `license_leases` was built with. A
 * 12-month lease is 360 days, not 365. Do not "fix" this to real calendar months:
 * it would make new leases inconsistent with the ones already running, and the
 * term is what the user signed for.
 */
export const LEASE_DAYS_PER_MONTH = 30;

/**
 * Term length used ONLY for the projected-return quote on
 * `/api/license-leases/network-stats`.
 *
 * Deliberately different from `LEASE_DAYS_PER_MONTH * months`: these are the
 * marketing term lengths the Next.js route quoted (180 / 365 / 547), and the
 * lease picker has always shown them. Kept apart from the figure that actually
 * sets `end_date` so neither can be changed by accident while editing the other.
 */
export const LEASE_PROJECTION_DAYS: Record<LeaseDurationMonths, number> = {
  6: 180,
  12: 365,
  18: 547,
};

/**
 * First `daily_rewards_snapshot.snapshot_date` (UTC, `YYYY-MM-DD`) on which a
 * lease locks the wallet's `referral_bonus` along with its regular reward.
 *
 * PRODUCT RULE (2026-09-30): any bonus that comes out of the daily reward stays
 * locked while the license it belongs to is locked. The daily job does NOT tie a
 * referral bonus to any of the wallet's own licenses — it is
 * `referred licenses × REFERRAL_BONUS_WEIGHT × bonusPerWeight`
 * (`apps/jobs/src/rewards/daily-rewards.ts`), whatever the wallet owns. The lock
 * therefore ATTRIBUTES it pro-rata to the wallet's own licenses (an
 * interpretation, not how the job computes it): leasing N of M licenses locks
 * N/M of that day's referral bonus exactly as it locks N/M of the regular
 * reward. This
 * reverses, going forward only, the 2026-09-23 rule (`bd637af`) that never
 * locked it. `multiple_license_bonus` has its own, later cutover:
 * {@link MULTIPLE_LICENSE_BONUS_LOCK_FROM_DAY}.
 *
 * Rows dated before this day keep the old rule (regular reward only) and are
 * not rewritten — no database row changes, and referral income a user could
 * already see as unlocked (and may already have claimed or queued) stays so.
 *
 * DECISION: the first snapshot written AFTER the decision, not the 2026-09-30
 * row. That row was written at ~00:02 UTC and was already claimable; locking it
 * retroactively would make the entitlement gate quarantine any claim already
 * queued against it — the same reasoning as the lease start-day rule (rule 4 in
 * `apps/api/src/lib/lease-semantics.ts`).
 * CONFIRM (product): the attribution above means a referrer whose only license
 * is leased has 100% of their referral income locked, while an identical
 * referrer who owns no license has none locked — the asymmetry `bd637af` cited
 * when it stopped locking the bonus. Accepted as the consequence of "its license
 * is locked"; sign-off needed on that consequence, not only on the headline rule.
 *
 * CONFIRM (deploy): this constant switches itself on at the 2026-10-01 snapshot
 * (00:00 UTC cron). Deploy `License Sale API` before then — it serves the figure
 * and runs this rule in its insert-time gate, so once it is live every new claim
 * already respects it. The dangerous order is a batcher (`License Sale Claim
 * Batcher`) running this rule while the OLD API still accepts claims against a
 * post-cutover row: those claims get quarantined by the referral bonus that
 * became locked. A late batcher alone is harmless (the old gate is looser). If
 * the API cannot ship before the cutover snapshot, bump this constant to the day
 * after the API deploy before shipping.
 *
 * Shared because TWO implementations of the lock rule must agree on it: the
 * API's `calculateUnlockedRewards` (what the dashboard shows and a claim may
 * ask for) and `findIneligibleClaims` in `packages/db` (what the batcher pays).
 */
export const REFERRAL_BONUS_LOCK_FROM_DAY = "2026-10-01";

/**
 * First `daily_rewards_snapshot.snapshot_date` (UTC, `YYYY-MM-DD`) on which a
 * lease also locks the wallet's `multiple_license_bonus`.
 *
 * PRODUCT RULE (2026-10-16): the multiple-license tier keeps counting EVERY
 * owned license, leased or not (the 2026-10-15 fix, `598aca7`), but the bonus a
 * leased license earns stays locked with it. Same attribution as the referral
 * bonus above: leasing N of M licenses locks N/M of that day's
 * `multiple_license_bonus` (pro-rata, not the marginal tier step the leased
 * licenses add), so a wallet with every license leased has all of it locked.
 * This reverses the 2026-10-15 team decision that kept the bonus claimable
 * mid-lease.
 * DECISION (product, 2026-10-16): pro-rata over the marginal alternative (lock
 * only the extra bonus the leased licenses add to the tier). The tier table is
 * not linear, so the two differ — 50 owned with 10 leased locks 20% here and
 * ~64% marginally — and pro-rata is the one both lock implementations already
 * compute for the other components. Signed off with the rule itself.
 *
 * Must not be earlier than {@link REFERRAL_BONUS_LOCK_FROM_DAY}: the lock adds
 * this bonus on top of regular + referral, it does not replace the referral step.
 *
 * DECISION: 2026-10-17, the first snapshot after the decision, not 2026-10-16.
 * The 2026-10-16 row was already claimable, and a pending claim on production
 * (wallet 0x69d613f0…) had spent 10.86 of its multiple-license bonus; locking
 * that row would have made the entitlement gate quarantine the claim. Rows dated
 * before this day keep the bonus unlocked and are not rewritten — that includes
 * 2026-10-16's 14,949.33 TRUTH across 207 wallets with a running lease.
 *
 * CONFIRM (deploy): switches itself on at the 2026-10-17 snapshot (00:00 UTC
 * cron). Deploy `License Sale API` before then, and before or with `License Sale
 * Claim Batcher` — the same order and reasoning as the referral cutover above.
 * If the API cannot ship before that snapshot, bump this constant to the day
 * after the API deploy before shipping.
 *
 * Shared for the same reason: `calculateUnlockedRewards` (apps/api) and
 * `findIneligibleClaims` (packages/db) must agree on it.
 */
export const MULTIPLE_LICENSE_BONUS_LOCK_FROM_DAY = "2026-10-17";
